import React, { useMemo, useState, useEffect } from "react";
import { Bar } from "react-chartjs-2";
import DateInputDDMMYYYY from "../../../components/DateInputDDMMYYYY";

interface Video3DViewsData {
  date: string;
  views: number;
}

interface Video3DViewsChartProps {
  data?: Video3DViewsData[];
}

const Video3DViewsChart: React.FC<Video3DViewsChartProps> = ({ data = [] }) => {
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const formatDateYYYYMMDD = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const subtractOneMonth = (date: Date) => {
    const d = new Date(date);
    const dayOfMonth = d.getDate();
    d.setDate(1);
    d.setMonth(d.getMonth() - 1);
    const lastDayOfTargetMonth = new Date(
      d.getFullYear(),
      d.getMonth() + 1,
      0
    ).getDate();
    d.setDate(Math.min(dayOfMonth, lastDayOfTargetMonth));
    return d;
  };

  const defaultEndDate = useMemo(() => formatDateYYYYMMDD(new Date()), []);
  const defaultStartDate = useMemo(
    () => formatDateYYYYMMDD(subtractOneMonth(new Date())),
    []
  );

  const [startDate, setStartDate] = useState<string>(defaultStartDate);
  const [endDate, setEndDate] = useState<string>(defaultEndDate);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Detect dark mode
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains('dark'));
    };
    
    checkDarkMode();
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    return () => observer.disconnect();
  }, []);

  // Mock data for demonstration - replace with actual API data
  const mockData: Video3DViewsData[] = useMemo(() => {
    const now = new Date();
    const data: Video3DViewsData[] = [];
    for (let i = 0; i < 90; i++) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      data.push({
        date: date.toISOString().split("T")[0],
        views: Math.floor(Math.random() * 1000) + 200,
      });
    }
    return data.reverse();
  }, []);

  const baseData = data.length > 0 ? data : mockData;

  // Filter data based on month and date range
  const filteredData = useMemo(() => {
    let filtered = [...baseData];

    // Filter by month if selected
    if (selectedMonth !== null) {
      filtered = filtered.filter((item) => {
        const date = new Date(item.date);
        return date.getMonth() + 1 === selectedMonth;
      });
    }

    // Filter by date range if provided
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      filtered = filtered.filter((item) => {
        const itemDate = new Date(item.date);
        return itemDate >= start && itemDate <= end;
      });
    }

    // Sort by date
    return filtered.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [baseData, selectedMonth, startDate, endDate]);

  // Prepare chart data
  const chartData = useMemo(() => {
    const labels = filteredData.map((item) => {
      const date = new Date(item.date);
      return date.toLocaleDateString("vi-VN", {
        month: "short",
        day: "numeric",
      });
    });

    const views = filteredData.map((item) => item.views);

    return {
      labels,
      datasets: [
        {
          label: "Lượt xem video 3D",
          data: views,
          backgroundColor: "rgba(34, 197, 94, 0.8)",
          borderColor: "rgba(34, 197, 94, 1)",
          borderWidth: 1,
          borderRadius: 4,
          borderSkipped: false,
        },
      ],
    };
  }, [filteredData]);

  const chartOptions: any = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: "top" as const,
        labels: {
          color: isDarkMode ? "#f3f4f6" : "#374151",
          font: {
            size: 12,
            weight: "600",
          },
        },
      },
      tooltip: {
        backgroundColor: isDarkMode ? "rgba(0, 0, 0, 0.9)" : "rgba(0, 0, 0, 0.8)",
        titleColor: "#fff",
        bodyColor: "#fff",
        borderColor: "rgba(34, 197, 94, 0.5)",
        borderWidth: 1,
        cornerRadius: 8,
        callbacks: {
          label: (context: any) => {
            return `Lượt xem: ${context.parsed.y}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Số lượt xem",
          font: {
            size: 14,
            weight: "600",
          },
          color: isDarkMode ? "#f3f4f6" : "#374151",
        },
        grid: {
          color: isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
        },
        ticks: {
          stepSize: 1,
          color: isDarkMode ? "#d1d5db" : "#6b7280",
        },
      },
      x: {
        title: {
          display: true,
          text: "Ngày",
          font: {
            size: 14,
            weight: "600",
          },
          color: isDarkMode ? "#f3f4f6" : "#374151",
        },
        grid: {
          display: false,
        },
        ticks: {
          maxTicksLimit: 15,
          color: isDarkMode ? "#d1d5db" : "#6b7280",
        },
      },
    },
    interaction: {
      mode: "nearest" as const,
      axis: "x" as const,
      intersect: false,
    },
    animation: {
      duration: 2000,
      easing: "easeInOutQuart" as const,
    },
  }), [isDarkMode]);

  return (
    <div className="bg-white dark:bg-gray-800 px-4 py-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm mb-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Biểu đồ Lượt Xem Video 3D
        </h3>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 mb-4">
          {/* Month Filter */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Chọn tháng:
            </label>
            <select
              value={selectedMonth === null ? "" : selectedMonth}
              onChange={(e) =>
                setSelectedMonth(e.target.value ? parseInt(e.target.value) : null)
              }
              className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="">Tất cả</option>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((month) => (
                <option key={month} value={month}>
                  Tháng {month}
                </option>
              ))}
            </select>
          </div>

          {/* Date Range Filter */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Từ ngày:
            </label>
            <DateInputDDMMYYYY
              valueISO={startDate}
              onChangeISO={setStartDate}
              fallbackISO={defaultStartDate}
              className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Đến ngày:
            </label>
            <DateInputDDMMYYYY
              valueISO={endDate}
              onChangeISO={setEndDate}
              fallbackISO={defaultEndDate}
              className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>

          {/* Clear Filters Button */}
          {(selectedMonth !== null ||
            startDate !== defaultStartDate ||
            endDate !== defaultEndDate) && (
            <button
              onClick={() => {
                setSelectedMonth(null);
                setStartDate(defaultStartDate);
                setEndDate(defaultEndDate);
              }}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
              <span>Xóa lọc</span>
            </button>
          )}
        </div>

        {/* Summary Stats */}
        {filteredData.length > 0 && (
          <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <div className="flex items-center gap-4 text-sm">
              <div>
                <span className="text-gray-600 dark:text-gray-300">Tổng lượt xem: </span>
                <span className="font-semibold text-green-600 dark:text-green-400">
                  {filteredData.reduce((sum, item) => sum + item.views, 0).toLocaleString()}
                </span>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-300">Trung bình/ngày: </span>
                <span className="font-semibold text-green-600 dark:text-green-400">
                  {Math.round(
                    filteredData.reduce((sum, item) => sum + item.views, 0) /
                      filteredData.length
                  ).toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="h-80">
        {filteredData.length > 0 ? (
          <Bar data={chartData} options={chartOptions} />
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
            Không có dữ liệu để hiển thị
          </div>
        )}
      </div>
    </div>
  );
};

export default Video3DViewsChart;
